/* src/pages/Acc.js */

import React, { useState, useEffect, useRef } from 'react'
import styles from '../styles/Pages.module.css'
import useDebounce from '../hooks/useDebounce'
import TransactionTable from '../components/TransactionTable.jsx'

export default function Acc() {
  // ─── Состояния для поиска и выбора аккаунтов ─────────────────────
  const [query, setQuery] = useState('')
  const [allAccounts, setAllAccounts] = useState([])
  const [filtered, setFiltered] = useState([])
  const [dropdownVisible, setDropdown] = useState(false)
  const [highlightedIndex, setHighlighted] = useState(-1)
  const [selected, setSelected] = useState(null)
  const [error, setError] = useState('')

  // ─── Транзакции ───────────────────────────────────────────────────
  const [txData, setTxData] = useState({
    total: 0,
    data: [],
    limit: 10,
    offset: 0,
  })

  // ─── Флаги активности мыши ────────────────────────────────────────
  const [mouseMoved, setMouseMoved] = useState(false)
  const mouseMoveTimeout = useRef(null)

  // Отслеживание активности мыши: мышь считается "активной" на 150мс
  // txData убран из зависимостей — эффект не использует его,
  // присутствие вызывало лишнюю перерегистрацию обработчика
  useEffect(() => {
    const handleMouseMove = () => {
      setMouseMoved(true)
      clearTimeout(mouseMoveTimeout.current)
      mouseMoveTimeout.current = setTimeout(() => {
        setMouseMoved(false)
      }, 150)
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      clearTimeout(mouseMoveTimeout.current)
    }
  }, []) // ← убран txData: эффект не читает его значение

  // ─── Загрузка транзакций при выборе аккаунта ─────────────────────
  useEffect(() => {
    if (!selected) return
    ;(async () => {
      try {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // txData.limit читается только при монтировании/смене selected,
        // добавление txData в deps вызвало бы бесконечный цикл (setTxData внутри)
        const { limit } = txData
        const res = await fetch(
          `/api/Transactions/${selected.clt_id}?limit=${limit}&offset=0`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        )
        if (!res.ok) throw new Error(`Ошибка ${res.status}`)
        const json = await res.json()
        setTxData({
          total: json.total,
          limit: json.limit,
          offset: 0,
          data: json.data.map(tx => ({
            ...tx,
            id: tx.transaction_id,
          })),
        })
      } catch (e) {
        setError(e.message)
      }
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]) // txData намеренно исключён во избежание бесконечного цикла

  // ─── Загрузка следующей порции транзакций при скролле ────────────
  const loadMore = async () => {
    if (!selected) return
    const nextOffset = txData.offset + txData.limit
    if (nextOffset >= txData.total) return

    try {
      const res = await fetch(
        `/api/Transactions/${selected.clt_id}?limit=${txData.limit}&offset=${nextOffset}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      )
      if (!res.ok) throw new Error(`Ошибка ${res.status}`)
      const json = await res.json()
      setTxData(prev => ({
        ...prev,
        offset: nextOffset,
        data: [
          ...prev.data,
          ...json.data.map(tx => ({
            ...tx,
            id: tx.transaction_id,
          })),
        ],
      }))
    } catch (e) {
      setError(e.message)
    }
  }

  // ─── Debounce поиска ──────────────────────────────────────────────
  const debouncedQuery = useDebounce(query, 300)

  const inputRef = useRef(null)
  const dropdownRef = useRef(null)

  // ─── Загрузка списка аккаунтов ────────────────────────────────────
  useEffect(() => {
    ;(async () => {
      try {
        const res = await fetch('/api/accs', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })
        if (!res.ok) throw new Error(`Ошибка ${res.status}`)
        const data = await res.json()
        setAllAccounts(data)
        setFiltered([...data].sort((a, b) => a.clt_name.localeCompare(b.clt_name)))
        inputRef.current?.focus()
      } catch (e) {
        setError(e.message)
      }
    })()
  }, [])

  // ─── Фильтрация по введённому значению ───────────────────────────
  useEffect(() => {
    const q = debouncedQuery.trim().toLowerCase()
    let results =
      q === ''
        ? [...allAccounts]
        : allAccounts.filter(acc =>
            acc.clt_name.toLowerCase().includes(q) ||
            acc.clt_account.toLowerCase().includes(q) ||
            (acc.account_description || '').toLowerCase().includes(q) ||
            (acc.tags || []).some(t => t.toLowerCase().includes(q)) ||
            (acc.clt_users || []).some(u => u.toString().includes(q))
          )

    results.sort((a, b) => a.clt_name.localeCompare(b.clt_name))
    setFiltered(results)
    setDropdown(true)
    setHighlighted(-1)
  }, [debouncedQuery, allAccounts])

  // ─── Обработка поля поиска ────────────────────────────────────────
  const handleChange = e => {
    setQuery(e.target.value)
    setSelected(null)
    setTxData({ ...txData, data: [], offset: 0, total: 0 })
  }

  const handleFocus = () => setDropdown(true)

  const handleBlur = e => {
    if (e.relatedTarget && dropdownRef.current?.contains(e.relatedTarget)) return
    setDropdown(false)
  }

  // ─── Клавиатурная навигация по выпадающему списку ────────────────
  const handleKeyDown = e => {
    if (!dropdownVisible || !filtered.length) return

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted(prev => {
        const delta = e.key === 'ArrowDown' ? 1 : -1
        let next = prev + delta
        if (next < 0) next = 0
        if (next >= filtered.length) next = filtered.length - 1

        const item = dropdownRef.current?.children[next]
        if (item) {
          const top = item.offsetTop
          const bottom = top + item.offsetHeight
          const scrollT = dropdownRef.current.scrollTop
          const viewB = scrollT + dropdownRef.current.clientHeight
          if (top < scrollT) dropdownRef.current.scrollTop = top
          else if (bottom > viewB)
            dropdownRef.current.scrollTop = bottom - dropdownRef.current.clientHeight
        }

        return next
      })
    } else if ((e.key === 'Enter' || e.key === 'Tab') && highlightedIndex >= 0) {
      e.preventDefault()
      selectAccount(filtered[highlightedIndex])
    } else if (e.key === 'Escape') {
      setDropdown(false)
    }
  }

  const selectAccount = acc => {
    setSelected(acc)
    setQuery(`${acc.clt_name} | ${acc.clt_account}`)
    setDropdown(false)
    setHighlighted(-1)
    setTxData({ total: 0, data: [], limit: 10, offset: 0 })
  }

  // ─── Рендер ───────────────────────────────────────────────────────
  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.headerTitle}>Accounts</h1>
      </header>

      <div className={styles.wrapper}>
        <input
          ref={inputRef}
          className={styles.input}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Account..."
          spellCheck={false}
          autoComplete="off"
          autoFocus
        />

        {dropdownVisible && filtered.length > 0 && (
          <ul ref={dropdownRef} className={styles.dropdown}>
            {filtered.map((acc, idx) => (
              <li
                key={acc.clt_id}
                tabIndex={0}
                className={`${styles.listItem} ${
                  idx === highlightedIndex ? styles.listItemHover : ''
                }`}
                onMouseEnter={() => {
                  // ❗ Подсвечивать только если мышь недавно двигалась
                  if (mouseMoved) setHighlighted(idx)
                }}
                onMouseDown={e => e.preventDefault()}
                onClick={() => selectAccount(acc)}
              >
                <span className={styles.nameSpan}>{acc.clt_name}</span>
                <span className={styles.accountSpan}>{acc.clt_account}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <>
          <TransactionTable
            data={txData.data}
            total={txData.total}
            limit={txData.limit}
            offset={txData.offset}
            onLoadMore={loadMore}
            onRowSelect={row => console.log('Row clicked', row)}
          />
        </>
      )}

      {error && <p className={styles.errorMsg}>{error}</p>}
    </div>
  )
}



